import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Boxes, CalendarDays, CheckCircle2, Download,
  FileText, Landmark, LoaderCircle, Mail, PieChart, RefreshCw, Send,
  TrendingUp, Users, Clock3, Sparkles, ShieldCheck, ChevronDown, FileCheck2,
} from "lucide-react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useAuth } from "@/lib/store";
import { useLocalization } from "@/lib/localization";
import { reportCopy, reportTypeCopy } from "@/lib/reportLocalization";
import { connectTelegram, createOnDemandReport, fetchOnDemandReport, fetchTelegramStatus } from "@/lib/shopApi";
import { ApiError } from "@/lib/apiClient";
import { toast } from "sonner";

const REPORT_TYPES = [
  { id: "sales", icon: TrendingUp, tone: "from-primary/[0.18] to-primary/[0.03]" },
  { id: "profit", icon: PieChart, tone: "from-emerald-500/[0.18] to-emerald-500/[0.03]" },
  { id: "inventory", icon: Boxes, tone: "from-amber-500/[0.18] to-amber-500/[0.03]" },
  { id: "customers", icon: Users, tone: "from-sky-500/[0.18] to-sky-500/[0.03]" },
  { id: "operations", icon: Landmark, tone: "from-rose-500/[0.18] to-rose-500/[0.03]" },
];

function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function presetRange(preset) {
  const end = new Date();
  const start = new Date(end);
  if (preset === "7days") start.setDate(start.getDate() - 6);
  if (preset === "30days") start.setDate(start.getDate() - 29);
  if (preset === "month") start.setDate(1);
  return { start: formatDate(start), end: formatDate(end) };
}

function validDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) || formatDate(date) !== value ? null : date;
}

function secureRequestKey() {
  return globalThis.crypto?.randomUUID?.() || "";
}

export default function Reports() {
  const { reportType } = useParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const { language, locale } = useLocalization();
  const copy = reportCopy[language] || reportCopy.en;
  const typeCopy = reportTypeCopy[language] || reportTypeCopy.en;
  const labels = typeCopy.map(([label]) => label);
  const selected = REPORT_TYPES.find((item) => item.id === reportType);
  const initialRange = useMemo(() => presetRange("30days"), []);
  const [preset, setPreset] = useState("30days");
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [channel, setChannel] = useState("email");
  const [email, setEmail] = useState(session?.email || "");
  const [telegram, setTelegram] = useState({ active: false, available: true });
  const [telegramBusy, setTelegramBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState(null);
  const requestKey = useRef(secureRequestKey());
  const inputSignature = `${reportType}|${startDate}|${endDate}|${channel}|${email}`;
  const previousInputSignature = useRef(inputSignature);
  const reportId = report?.id;
  const reportStatus = report?.status;

  useEffect(() => {
    if (previousInputSignature.current !== inputSignature) {
      requestKey.current = secureRequestKey();
      previousInputSignature.current = inputSignature;
    }
  }, [inputSignature]);

  useEffect(() => {
    if (!reportType || selected) return;
    navigate("/reports", { replace: true });
  }, [navigate, reportType, selected]);

  useEffect(() => {
    if (!session?.sessionId || !selected) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const result = await fetchTelegramStatus(session.sessionId);
        if (!cancelled) setTelegram(result.telegram || { active: false, available: false });
      } catch {
        if (!cancelled) setTelegram({ active: false, available: false });
      }
    };
    void refresh();
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    return () => { cancelled = true; window.removeEventListener("focus", onFocus); };
  }, [selected, session?.sessionId]);

  useEffect(() => {
    if (!reportId || !["queued", "processing"].includes(reportStatus)) return;
    let cancelled = false;
    let timer;
    let failures = 0;
    const poll = async () => {
      try {
        const result = await fetchOnDemandReport(session.sessionId, reportId);
        if (cancelled) return;
        failures = 0;
        const next = result.report;
        setReport(next);
        if (["queued", "processing"].includes(next.status)) timer = window.setTimeout(poll, 2000);
      } catch {
        if (cancelled) return;
        failures += 1;
        timer = window.setTimeout(poll, Math.min(15000, 2000 * (2 ** Math.min(failures, 3))));
      }
    };
    timer = window.setTimeout(poll, 1200);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [reportId, reportStatus, session?.sessionId]);

  function choosePreset(next) {
    setPreset(next);
    if (next !== "custom") {
      const range = presetRange(next);
      setStartDate(range.start);
      setEndDate(range.end);
    }
  }

  async function handleTelegramConnect() {
    setTelegramBusy(true);
    const popup = window.open("about:blank", "shopbook-telegram-connect");
    if (popup) popup.opener = null;
    try {
      const result = await connectTelegram(session.sessionId);
      if (popup) popup.location.href = result.telegram.connectUrl;
      else window.location.assign(result.telegram.connectUrl);
      toast.success(copy.telegramOpen);
    } catch (error) {
      popup?.close();
      toast.error(error?.message || copy.telegramFailed);
    } finally {
      setTelegramBusy(false);
    }
  }

  async function submit(event) {
    event.preventDefault();
    const start = validDate(startDate);
    const end = validDate(endDate);
    const days = start && end ? Math.floor((end.getTime() - start.getTime()) / 86400000) + 1 : 0;
    if (!start || !end || days < 1 || days > 366 || end.getTime() > Date.now()) {
      toast.error(copy.invalidDates);
      return;
    }
    if (channel === "email" && !/^\S+@\S+\.\S+$/.test(email.trim())) {
      toast.error(copy.invalidEmail);
      return;
    }
    if (channel === "telegram" && !telegram.active) {
      toast.error(copy.telegramRequired);
      return;
    }
    if (!requestKey.current) {
      toast.error(copy.secureBrowser);
      return;
    }
    setSubmitting(true);
    try {
      const result = await createOnDemandReport(session.sessionId, {
        deliveryChannel: channel,
        email: channel === "email" ? email.trim() : undefined,
        endDate,
        idempotencyKey: requestKey.current,
        language,
        reportType: selected.id,
        startDate,
        timezoneOffsetMinutes: new Date().getTimezoneOffset(),
      });
      setReport(result.report);
    } catch (error) {
      if (error instanceof ApiError && error.code === "TELEGRAM_NOT_CONNECTED") {
        setTelegram((current) => ({ ...current, active: false }));
      }
      toast.error(error?.message || copy.queueFailed);
    } finally {
      setSubmitting(false);
    }
  }

  if (!selected) return <ReportList copy={copy} navigate={navigate} typeCopy={typeCopy} />;

  const title = labels[REPORT_TYPES.findIndex((item) => item.id === selected.id)];
  if (report) {
    const pending = report.status === "queued" || report.status === "processing";
    const failed = report.status === "failed";
    return (
      <PageContainer>
        <div className="mx-auto max-w-3xl">
          <Button variant="ghost" onClick={() => navigate("/reports")} className="rounded-xl"><ArrowLeft className="h-4 w-4" /> {copy.back}</Button>
          <section className="luxe-card mt-5 overflow-hidden rounded-[28px] border bg-card p-8 text-center md:p-12">
            <div className={`mx-auto flex h-20 w-20 items-center justify-center rounded-[26px] ${failed ? "bg-destructive/10 text-destructive" : pending ? "bg-primary/10 text-primary" : "bg-emerald-500/10 text-emerald-600"}`}>
              {pending ? <LoaderCircle className="h-9 w-9 animate-spin" /> : failed ? <RefreshCw className="h-9 w-9" /> : <CheckCircle2 className="h-10 w-10" />}
            </div>
            <h1 className="mt-6 text-3xl font-semibold tracking-[-0.035em]">{title}</h1>
            <p className="mx-auto mt-2 max-w-lg text-sm leading-6 text-muted-foreground">
              {pending ? copy.queued : failed ? copy.failed : copy.delivered(report.deliveryChannel)}
            </p>
            <div className="mt-7 flex flex-wrap justify-center gap-3">
              {!pending && !failed && report.downloadUrl ? <Button asChild className="h-11 rounded-xl px-5"><a href={report.downloadUrl} target="_blank" rel="noreferrer"><Download className="h-4 w-4" /> {copy.openPDF}</a></Button> : null}
              {failed ? <Button className="h-11 rounded-xl px-5" onClick={() => { requestKey.current = secureRequestKey(); setReport(null); }}><RefreshCw className="h-4 w-4" /> {copy.tryAgain}</Button> : null}
              <Button variant="outline" className="h-11 rounded-xl px-5" onClick={() => navigate("/reports")}>{copy.chooseAnother}</Button>
            </div>
          </section>
        </div>
      </PageContainer>
    );
  }

  const selectedStart = validDate(startDate);
  const selectedEnd = validDate(endDate);
  const rangeDays = selectedStart && selectedEnd ? Math.floor((selectedEnd.getTime() - selectedStart.getTime()) / 86400000) + 1 : 0;
  const readableStart = selectedStart?.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" }) || copy.chooseDate;
  const readableEnd = selectedEnd?.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" }) || copy.chooseDate;

  return (
    <PageContainer className="pb-12">
      <div className="mx-auto max-w-6xl">
        <Button variant="ghost" onClick={() => navigate("/reports")} className="-ml-3 h-9 rounded-xl text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> {copy.back}</Button>

        <header className="mt-3 border-b border-border/70 pb-5">
          <div className="flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-primary"><FileText className="h-3.5 w-3.5" /> {copy.delivery}</div>
          <h1 className="mt-2 text-3xl font-semibold tracking-[-0.04em]">{title}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{copy.deliveryIntro}</p>
        </header>

        <form onSubmit={submit} className="mt-7 grid items-start gap-5 lg:grid-cols-[minmax(0,1.35fr)_minmax(340px,0.65fr)]">
          <Card className="overflow-hidden rounded-[26px] border-border/80 bg-card/90 shadow-[0_24px_60px_-42px_rgba(15,23,42,0.55)] backdrop-blur-xl">
            <CardHeader className="border-b border-border/70 bg-gradient-to-r from-primary/[0.055] via-transparent to-transparent px-6 py-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-2xl border border-primary/10 bg-background/80 text-primary shadow-sm"><CalendarDays className="h-5 w-5" /></span><div><CardTitle className="text-base">{copy.buildWindow}</CardTitle><CardDescription className="mt-1 text-xs">{copy.buildWindowHint}</CardDescription></div></div>
                <Badge variant="outline" className="rounded-full border-border/80 bg-background/60 px-3 py-1.5 font-medium"><Clock3 className="mr-1.5 h-3 w-3 text-primary" />{copy.dayCount(rangeDays || 0)}</Badge>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{copy.quickWindows}</Label>
              <ToggleGroup type="single" value={preset} onValueChange={(value) => value && choosePreset(value)} variant="outline" className="mt-3 grid grid-cols-2 gap-2 rounded-2xl bg-muted/35 p-1.5 sm:grid-cols-4">
                {[["7days", copy.last7], ["30days", copy.last30], ["month", copy.thisMonth], ["custom", copy.custom]].map(([id, label]) => <ToggleGroupItem key={id} value={id} className="h-10 rounded-xl border-0 text-xs text-muted-foreground transition-all hover:bg-background/70 hover:text-foreground data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:shadow-[0_8px_20px_-12px_hsl(var(--primary))] data-[state=on]:hover:bg-primary data-[state=on]:hover:text-primary-foreground">{label}</ToggleGroupItem>)}
              </ToggleGroup>

              <div className="mt-7 grid items-center gap-3 md:grid-cols-[1fr_auto_1fr]">
                <DatePickerField copy={copy} label={copy.starts} value={selectedStart} display={readableStart} onChange={(date) => { setStartDate(formatDate(date)); setPreset("custom"); }} />
                <div className="hidden items-center md:flex"><div className="h-px w-5 bg-border" /><span className="flex h-8 w-8 items-center justify-center rounded-full border bg-muted/40 text-muted-foreground"><ArrowRight className="h-3.5 w-3.5" /></span><div className="h-px w-5 bg-border" /></div>
                <DatePickerField copy={copy} label={copy.ends} value={selectedEnd} display={readableEnd} onChange={(date) => { setEndDate(formatDate(date)); setPreset("custom"); }} />
              </div>

              <div className="relative mt-7 overflow-hidden rounded-[20px] border border-primary/10 bg-gradient-to-r from-primary/[0.08] via-primary/[0.035] to-transparent p-5">
                <div className="absolute right-0 top-0 h-24 w-24 rounded-full bg-primary/[0.08] blur-2xl" />
                <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div><div className="flex items-center gap-2 text-xs font-semibold text-primary"><Sparkles className="h-3.5 w-3.5" /> {copy.preview}</div><div className="mt-2 text-lg font-semibold tracking-tight">{readableStart} <span className="px-1 text-muted-foreground">→</span> {readableEnd}</div><p className="mt-1 text-xs text-muted-foreground">{copy.timezoneHint}</p></div>
                  <div className="flex shrink-0 items-center gap-2"><span className="rounded-xl border bg-background/75 px-3 py-2 text-center shadow-sm"><span className="block text-lg font-semibold text-primary">{rangeDays || "—"}</span><span className="text-[9px] uppercase tracking-wider text-muted-foreground">{copy.days}</span></span><span className="rounded-xl border bg-background/75 px-3 py-2 text-center shadow-sm"><span className="block text-lg font-semibold text-primary">5</span><span className="text-[9px] uppercase tracking-wider text-muted-foreground">{copy.insightAreas}</span></span></div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="overflow-hidden rounded-[26px] border-border/80 bg-card/95 shadow-[0_24px_60px_-42px_rgba(15,23,42,0.65)] lg:sticky lg:top-20">
            <CardHeader className="px-5 pb-4 pt-5"><div className="flex items-start justify-between"><div><CardTitle className="text-base">{copy.chooseRoute}</CardTitle><CardDescription className="mt-1 text-xs">{copy.routeHint}</CardDescription></div><span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Send className="h-4 w-4" /></span></div></CardHeader>
            <CardContent className="px-5 pb-5">
              <RadioGroup value={channel} onValueChange={setChannel} className="grid grid-cols-2 gap-3">
                <DeliveryOption id="email" active={channel === "email"} icon={Mail} label={copy.email} meta={copy.inboxDelivery} selectedLabel={copy.selected} />
                <DeliveryOption id="telegram" active={channel === "telegram"} icon={Send} label={copy.telegram} meta={copy.connectedChat} selectedLabel={copy.selected} telegram />
              </RadioGroup>

              <div className="mt-5">
                {channel === "email" ? <div className="space-y-2"><Label htmlFor="report-email" className="text-xs">{copy.recipientEmail}</Label><div className="relative"><Mail className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input id="report-email" type="email" autoComplete="email" maxLength={320} value={email} onChange={(event) => setEmail(event.target.value)} className="h-11 rounded-xl bg-muted/20 pl-10" /></div></div> : <div className="flex items-center gap-3 rounded-2xl border bg-muted/25 p-3.5"><span className={`relative flex h-10 w-10 items-center justify-center rounded-xl ${telegram.active ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"}`}><Send className="h-4 w-4" /><span className={`absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-card ${telegram.active ? "bg-emerald-500" : "bg-amber-500"}`} /></span><div className="min-w-0 flex-1"><div className="text-xs font-semibold">{telegram.active ? copy.telegramReady : copy.connectionRequired}</div><div className="mt-0.5 truncate text-[11px] text-muted-foreground">{telegram.chatLabel || copy.connectChat}</div></div>{!telegram.active ? <Button type="button" size="sm" className="rounded-lg" disabled={telegramBusy || !telegram.available} onClick={handleTelegramConnect}>{telegramBusy ? <LoaderCircle className="animate-spin" /> : copy.connect}</Button> : <CheckCircle2 className="h-4 w-4 text-emerald-500" />}</div>}
              </div>

              <Separator className="my-5" />
              <div className="space-y-3">
                <SummaryRow label={copy.report} value={title} icon={FileCheck2} />
                <SummaryRow label={copy.window} value={copy.dayCount(rangeDays || 0)} icon={CalendarDays} />
                <SummaryRow label={copy.format} value="PDF" icon={Sparkles} />
              </div>

              <Button type="submit" disabled={submitting || (channel === "telegram" && !telegram.active)} className="group mt-5 h-12 w-full rounded-xl text-sm shadow-[0_15px_32px_-16px_hsl(var(--primary))]">{submitting ? <LoaderCircle className="animate-spin" /> : <Sparkles className="transition-transform group-hover:rotate-12" />} {submitting ? copy.preparing : copy.generate}</Button>
              <div className="mt-3 flex items-start justify-center gap-2 px-2 text-center text-[10px] leading-4 text-muted-foreground"><ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" /><span>{copy.queuedSecurely}</span></div>
            </CardContent>
          </Card>
        </form>
      </div>
    </PageContainer>
  );
}

function ReportList({ copy, navigate, typeCopy }) {
  return (
    <PageContainer>
      <div className="mx-auto max-w-6xl">
        <header><div className="inline-flex items-center gap-2 rounded-full border border-primary/15 bg-primary/[0.06] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-primary"><FileText className="h-3.5 w-3.5" /> {copy.centre}</div><h1 className="mt-3 text-3xl font-semibold tracking-[-0.035em]">{copy.heading}</h1><p className="mt-1 text-sm text-muted-foreground">{copy.intro}</p></header>
        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          {REPORT_TYPES.map((item, index) => { const Icon = item.icon; const [label, description, tags] = typeCopy[index]; return <button key={item.id} type="button" onClick={() => navigate(`/reports/${item.id}`)} className={`group relative overflow-hidden rounded-[22px] border bg-gradient-to-br ${item.tone} p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary`}><div className="flex items-start gap-4"><span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/50 bg-background/80 text-primary shadow-sm"><Icon className="h-5 w-5" /></span><div className="min-w-0 flex-1"><div className="flex items-center gap-2"><span className="text-[10px] font-bold tracking-[0.18em] text-muted-foreground">{String(index + 1).padStart(2, "0")}</span><h2 className="text-lg font-semibold tracking-tight">{label}</h2></div><p className="mt-1.5 text-sm leading-6 text-muted-foreground">{description}</p><div className="mt-3 flex flex-wrap gap-1.5">{tags.map((tag) => <span key={tag} className="rounded-full border bg-background/[0.55] px-2.5 py-1 text-[10px] font-medium text-muted-foreground">{tag}</span>)}</div></div><ArrowRight className="mt-2 h-4 w-4 shrink-0 text-muted-foreground transition group-hover:translate-x-1 group-hover:text-primary" /></div></button>; })}
        </div>
      </div>
    </PageContainer>
  );
}

function DatePickerField({ copy, display, label, onChange, value }) {
  return <div><Label className="mb-2 block text-xs">{label}</Label><Popover><PopoverTrigger asChild><Button type="button" variant="outline" className="h-auto w-full justify-between rounded-2xl bg-background px-4 py-3 text-left shadow-sm hover:bg-muted/30"><span className="flex items-center gap-3"><span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary"><CalendarDays className="h-4 w-4" /></span><span><span className="block text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{copy.dateLabel(label)}</span><span className="mt-0.5 block text-sm font-semibold">{display}</span></span></span><ChevronDown className="h-4 w-4 text-muted-foreground" /></Button></PopoverTrigger><PopoverContent align="start" className="w-auto rounded-2xl p-1"><Calendar mode="single" selected={value || undefined} onSelect={(date) => date && onChange(date)} disabled={(date) => date > new Date()} initialFocus /></PopoverContent></Popover></div>;
}

function DeliveryOption({ active, icon: Icon, id, label, meta, selectedLabel, telegram = false }) {
  return <Label htmlFor={`delivery-${id}`} className={`relative cursor-pointer rounded-2xl border p-3.5 transition-all ${active ? telegram ? "border-sky-500 bg-sky-500/[0.1] ring-1 ring-sky-500/20 shadow-[0_10px_28px_-20px_rgba(14,165,233,0.9)]" : "border-primary bg-primary/[0.1] ring-1 ring-primary/20 shadow-[0_10px_28px_-20px_hsl(var(--primary))]" : "bg-muted/[0.18] hover:border-primary/25 hover:bg-muted/35"}`}><div className="flex items-start justify-between"><span className={`flex h-9 w-9 items-center justify-center rounded-xl ${active ? telegram ? "bg-sky-500 text-white" : "bg-primary text-primary-foreground" : telegram ? "bg-sky-500/10 text-sky-500" : "bg-primary/10 text-primary"}`}><Icon className="h-4 w-4" /></span><RadioGroupItem id={`delivery-${id}`} value={id} /></div><div className="mt-3 flex items-center gap-2 text-xs font-semibold">{label}{active ? <span className={`rounded-full px-1.5 py-0.5 text-[8px] uppercase tracking-wider ${telegram ? "bg-sky-500/15 text-sky-600" : "bg-primary/15 text-primary"}`}>{selectedLabel}</span> : null}</div><div className="mt-0.5 text-[10px] font-normal text-muted-foreground">{meta}</div></Label>;
}

function SummaryRow({ icon: Icon, label, value }) {
  return <div className="flex items-center gap-3 text-xs"><span className="flex h-8 w-8 items-center justify-center rounded-xl bg-muted/55 text-muted-foreground"><Icon className="h-3.5 w-3.5" /></span><span className="text-muted-foreground">{label}</span><span className="ml-auto max-w-[170px] truncate font-semibold">{value}</span></div>;
}
